import { useQueryClient } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { http, refreshSession } from '@/lib/api/http';
import { ApiError } from '@/lib/api/errors';
import { session, setUnauthenticatedHandler } from '@/lib/api/session';

export interface Company { id: string; name: string }
export interface Me { userId: string; tenantId: string; companyId: string; roles: string[]; permissions: Record<string, string>; mustChangePassword?: boolean; mfaEnrollmentRequired?: boolean }
interface Tokens { accessToken: string; expiresIn: number; companyId: string; companies: Company[]; mustChangePassword?: boolean }

/** The companies of the signed-in user (names only), kept so that a page reload can show the company switcher again. */
const COMPANIES_KEY = 'auth.companies';
const savedCompanies = (): Company[] => { try { return JSON.parse(localStorage.getItem(COMPANIES_KEY) ?? '[]') as Company[]; } catch { return []; } };
const saveCompanies = (c: Company[] | null) => { try { if (c) localStorage.setItem(COMPANIES_KEY, JSON.stringify(c)); else localStorage.removeItem(COMPANIES_KEY); } catch { /* storage unavailable */ } };
interface MfaChallenge { mfaRequired: true; mfaToken: string; mfaExpiresIn: number }
export type LoginOutcome = 'ok' | 'mfa';

export type Scope = 'COMPANY' | 'OWN' | 'TENANT' | 'BRANCH' | 'TEAM';
const RANK: Record<string, number> = { OWN: 1, TEAM: 2, BRANCH: 3, COMPANY: 4, TENANT: 5 };

/** UI permission check. It is comfort only: the server re-checks everything. */
export function can(me: Me | null | undefined, code: string, atLeast: Scope = 'COMPANY'): boolean {
  const granted = me?.permissions?.[code];
  if (!granted) return false;
  return (RANK[granted] ?? 0) >= (RANK[atLeast] ?? 0);
}

interface AuthState {
  status: 'anonymous' | 'authenticated';
  me: Me | null;
  companies: Company[];
  company: Company | null;
  /** Throws ApiError; a 409 carries `companies` in `details` when a company must be chosen. */
  login(email: string, password: string, companyId?: string): Promise<LoginOutcome>;
  /** Opens a support session from the platform console's one-time ticket. */
  supportLogin(ticket: string): Promise<void>;
  /** Second step for accounts with two-factor authentication: the 6-digit code or a recovery code. */
  verifyMfa(code: string): Promise<void>;
  /** Re-read the profile (after the password was changed, for instance). */
  reloadMe(): Promise<void>;
  logout(): Promise<void>;
  sessionExpired: boolean;
  /** A token exists but the profile is still loading (page reload): render a splash, never a half-permissioned UI. */
  restoring: boolean;
}

const Ctx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const token = useSyncExternalStore(session.subscribe, () => session.token, () => null);
  const [me, setMe] = useState<Me | null>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [expired, setExpired] = useState(false);
  const [challenge, setChallenge] = useState<string | null>(null);

  useEffect(() => {
    setUnauthenticatedHandler(() => { setMe(null); setExpired(true); qc.clear(); });
  }, [qc]);

  // Restore the profile when a token already exists (page reload: main.tsx exchanged the refresh cookie before the first render).
  useEffect(() => {
    if (!session.token || me) return;
    http.get<Me>('/auth/me').then((p) => { setMe(p); setCompanies(savedCompanies()); }).catch(() => session.clear());
  }, []);                                                                          // eslint-disable-line react-hooks/exhaustive-deps

  // Cross-tab logout.
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return;
    const ch = new BroadcastChannel('crm-auth');
    ch.onmessage = (e) => { if (e.data === 'logout') { session.clear(); setMe(null); qc.clear(); } };
    return () => ch.close();
  }, [qc]);

  const finish = useCallback(async (r: Tokens) => {
    session.set(r.accessToken, r.expiresIn);
    const profile = await http.get<Me>('/auth/me');
    setCompanies(r.companies ?? []);
    saveCompanies(r.companies ?? []);
    setMe(profile); setExpired(false); setChallenge(null);
    qc.clear();
  }, [qc]);

  const login = useCallback(async (email: string, password: string, companyId?: string): Promise<LoginOutcome> => {
    const r = await http.post<Tokens | MfaChallenge>('/auth/login', { email, password, ...(companyId ? { companyId } : {}) }, { public: true });
    if ('mfaRequired' in r) { setChallenge(r.mfaToken); return 'mfa'; }
    await finish(r);
    return 'ok';
  }, [finish]);

  const verifyMfa = useCallback(async (code: string) => {
    if (!challenge) throw new Error('No second-factor challenge in progress');
    await finish(await http.post<Tokens>('/auth/mfa/verify', { mfaToken: challenge, code }, { public: true }));
  }, [challenge, finish]);

  const supportLogin = useCallback(async (ticket: string) => {
    await finish(await http.post<Tokens>('/auth/support-ticket', { ticket }, { public: true }));
  }, [finish]);

  const reloadMe = useCallback(async () => { setMe(await http.get<Me>('/auth/me')); }, []);

  // Renew the access token a minute before it expires, so users never meet a 401 in the middle of typing. (The HTTP layer also repairs a surprise 401.)
  useEffect(() => {
    if (!token) return;
    const wait = Math.max(5_000, session.expiresAt - Date.now() - 60_000);
    const id = setTimeout(() => { void refreshSession(); }, wait);
    return () => clearTimeout(id);
  }, [token, session.expiresAt]);                                                       // eslint-disable-line react-hooks/exhaustive-deps

  const logout = useCallback(async () => {
    try { await http.post('/auth/logout'); } catch (e) { if (!(e instanceof ApiError)) throw e; }
    session.clear(); saveCompanies(null); setMe(null); setExpired(false); qc.clear();
    if (typeof BroadcastChannel !== 'undefined') { const ch = new BroadcastChannel('crm-auth'); ch.postMessage('logout'); ch.close(); }
  }, [qc]);

  const value = useMemo<AuthState>(() => ({
    status: token && me ? 'authenticated' : 'anonymous', me, companies, company: companies.find((c) => c.id === me?.companyId) ?? null, login, supportLogin, verifyMfa, reloadMe, logout, sessionExpired: expired, restoring: !!token && !me,
  }), [token, me, companies, login, supportLogin, verifyMfa, reloadMe, logout, expired]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth outside AuthProvider');
  return v;
}

export const usePermission = (code: string, atLeast: Scope = 'COMPANY') => can(useAuth().me, code, atLeast);

/** Renders children only when the permission is held (or the fallback). Hiding is comfort, not security. */
export function Can({ permission, scope, children, fallback = null }: { permission: string; scope?: Scope; children: ReactNode; fallback?: ReactNode }) {
  return <>{usePermission(permission, scope) ? children : fallback}</>;
}
